import React, { createContext, useReducer, useContext, useEffect } from 'react';

type ThemeMode = 'light' | 'dark';

interface ThemeState {
  mode: ThemeMode;
}

type ThemeAction = { type: 'TOGGLE_THEME' } | { type: 'SET_THEME'; payload: ThemeMode };

const initialState: ThemeState = {
  mode: 'dark' // Default to dark mode
};

// Create the context
const ThemeContext = createContext<{
  state: ThemeState;
  dispatch: React.Dispatch<ThemeAction>;
}>({
  state: initialState,
  dispatch: () => null,
});

// Create the reducer
const themeReducer = (state: ThemeState, action: ThemeAction): ThemeState => {
  switch (action.type) {
    case 'TOGGLE_THEME':
      const newMode = state.mode === 'light' ? 'dark' : 'light';
      localStorage.setItem('theme', newMode);
      return { ...state, mode: newMode };
    case 'SET_THEME':
      return { ...state, mode: action.payload };
    default:
      return state;
  }
};

// Create the provider component
export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(themeReducer, initialState);

  // Initialize theme from localStorage or system preference
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      dispatch({ type: 'SET_THEME', payload: savedTheme as ThemeMode });
    } else {
      // Check for system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDark) {
        dispatch({ type: 'SET_THEME', payload: 'dark' });
      }
    }
  }, []);

  // Apply theme to document when it changes
  useEffect(() => {
    // Apply data-theme attribute for CSS variables
    document.documentElement.setAttribute('data-theme', state.mode);
    
    // For backwards compatibility with older code using classList
    if (state.mode === 'dark') {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
    }
    
    // Add a small delay to allow CSS transitions to work properly
    setTimeout(() => {
      document.body.style.transition = 'background-color 0.3s ease, color 0.3s ease';
    }, 100);
  }, [state.mode]);

  return (
    <ThemeContext.Provider value={{ state, dispatch }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Create a custom hook to use the context
export const useTheme = () => useContext(ThemeContext);

// Utility functions
export const toggleTheme = (dispatch: React.Dispatch<ThemeAction>) => {
  dispatch({ type: 'TOGGLE_THEME' });
}; 